import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import CohortPicker from './CohortPicker';
import ProofModal from './ProofModal';
import {
  jobsError,
  useCampaigns,
  useRecommendations,
  useStudentJobs,
} from '../../hooks/useJobDigest';
import { Badge, Card, count, date, Empty, fullName, INPUT, Pager } from './ui';

const LIMIT = 50;

function Proofs({ row }) {
  const parts = [];
  if (row.proofs_approved)
    parts.push(
      <Badge
        key="a"
        status="approved"
        label={`${row.proofs_approved} approved`}
      />
    );
  if (row.proofs_pending)
    parts.push(
      <Badge key="p" status="pending" label={`${row.proofs_pending} pending`} />
    );
  if (row.proofs_rejected)
    parts.push(
      <Badge
        key="r"
        status="rejected"
        label={`${row.proofs_rejected} rejected`}
      />
    );
  return parts.length ? (
    <div className="flex flex-wrap gap-1">{parts}</div>
  ) : (
    <span className="text-xs text-gray-400">None</span>
  );
}

function Hired({ row }) {
  if (row.hired === null || row.hired === undefined)
    return <span className="text-xs text-gray-400">No answer</span>;
  if (!row.hired) return <span className="text-xs text-gray-600">Not yet</span>;
  return (
    <span className="text-xs font-medium text-green-700">
      Yes{row.hired_via_digest ? ', from our emails' : ''}
    </span>
  );
}

function StudentJobs({ student, filters, onClose }) {
  const { data, isLoading, error } = useStudentJobs(
    student?.student_id,
    filters
  );
  const [proof, setProof] = useState(null);
  return (
    <Modal
      modalOpen={Boolean(student)}
      title={student ? `Jobs sent to ${fullName(student)}` : ''}
      wide
      onClose={onClose}
    >
      {student && (
        <div className="space-y-3 text-sm">
          <div className="text-xs text-gray-500">
            {student.email} · {student.cohort || student.cohort_id}
          </div>
          {isLoading && <p className="text-gray-500">Loading…</p>}
          {error && (
            <p className="text-red-700">
              {jobsError(error, "Couldn't load this student's jobs.")}
            </p>
          )}
          {data?.items?.length === 0 && (
            <Empty>No jobs under these filters.</Empty>
          )}
          <ul className="space-y-2">
            {data?.items?.map((item) => (
              <li
                key={item.item_id}
                className="rounded-lg border border-gray-200 px-4 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="font-medium text-claret-500 hover:underline"
                    >
                      {item.title}
                    </a>
                    <div className="text-xs text-gray-600">
                      {[item.company, item.location]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {item.why && (
                      <div className="mt-1 text-xs text-green-700">
                        ✓ {item.why}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs text-gray-500 space-y-1">
                    <div>
                      <Badge status={item.digest_status} /> {date(item.sent_at)}
                    </div>
                    <div>
                      {item.clicked_at
                        ? `Opened ${date(item.clicked_at)}${
                            item.clicks > 1 ? ` (${item.clicks}×)` : ''
                          }`
                        : 'Not opened'}
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  {item.application ? (
                    <>
                      <Badge status={item.application.status} />
                      <button
                        type="button"
                        className="text-claret-500 hover:underline"
                        onClick={() =>
                          setProof({
                            application: item.application,
                            job: item,
                          })
                        }
                      >
                        View proof
                      </button>
                    </>
                  ) : (
                    <span className="text-gray-400">No proof of applying</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ProofModal
        application={proof?.application}
        job={proof?.job}
        student={data?.student}
        onClose={() => setProof(null)}
      />
    </Modal>
  );
}

export default function RecommendationsTab({
  cohortIds,
  setCohortIds,
  campaignId,
  setCampaignId,
}) {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(null);
  const { data: campaigns = [] } = useCampaigns();

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setOffset(0), [cohortIds, campaignId, status, q]);

  const filters = { cohortIds, campaignId, status, q, limit: LIMIT, offset };
  const { data, isLoading, isFetching, error } = useRecommendations(filters);
  const students = data?.students ?? [];

  return (
    <div className="space-y-5">
      <Card>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2">
            <CohortPicker value={cohortIds} onChange={setCohortIds} />
          </div>
          <select
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
            className={INPUT}
          >
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.dry_run ? ' (dry run)' : ''}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className={INPUT}
          >
            <option value="">Sent and dry runs</option>
            <option value="sent">Sent only</option>
            <option value="dry_run">Dry runs only</option>
            <option value="failed">Failed to send</option>
            <option value="test">Test emails</option>
          </select>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name or email"
            className={`${INPUT} lg:col-span-2`}
          />
        </div>
      </Card>

      <Card
        title="Students"
        aside={
          data && (
            <span className="text-xs text-gray-500">
              {count(data.total)} students{isFetching ? ' · updating…' : ''}
            </span>
          )
        }
      >
        {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {error && (
          <p className="text-sm text-red-700">
            {jobsError(error, "Couldn't load recommendations.")}
          </p>
        )}
        {data && students.length === 0 && (
          <Empty>
            No jobs have been sent under these filters yet. Start a campaign
            from the Campaigns tab.
          </Empty>
        )}
        {students.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">Student</th>
                  <th className="py-2 pr-3">Cohort</th>
                  <th className="py-2 pr-3 text-right">Jobs sent</th>
                  <th className="py-2 pr-3 text-right">Opened</th>
                  <th className="py-2 pr-3">Proof of applying</th>
                  <th className="py-2 pr-3">Got a job</th>
                  <th className="py-2">Last sent</th>
                </tr>
              </thead>
              <tbody>
                {students.map((row) => (
                  <tr
                    key={`${row.student_id}-${row.cohort_id}`}
                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setOpen(row)}
                  >
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900">
                        {fullName(row)}
                      </div>
                      <div className="text-xs text-gray-500">{row.email}</div>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-700">
                      {row.cohort || row.cohort_id || '—'}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {count(row.jobs_sent)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {count(row.jobs_clicked)}
                    </td>
                    <td className="py-2 pr-3">
                      <Proofs row={row} />
                    </td>
                    <td className="py-2 pr-3">
                      <Hired row={row} />
                    </td>
                    <td className="py-2 text-xs text-gray-500">
                      <Badge status={row.last_digest_status} />{' '}
                      {date(row.last_sent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager
          total={data?.total}
          limit={LIMIT}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <StudentJobs
        student={open}
        filters={{ cohortIds, campaignId, status }}
        onClose={() => setOpen(null)}
      />
    </div>
  );
}
